import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";

import { fetchTools } from "../../redux/modules/action.creators/app.actions";
import { contractsSelector } from "../../redux/modules/app.module";

import { ContactArticle } from "../../components/contact.article.component";
import Footer from "../../components/footer.component";

import { toSlug, getFileName, downloadFile } from "../../utils/app.helpers/slug";

import styles from "./styles.module.scss";

interface IContractItem {
  slug: string;
  title: string;
  url: string;
  fileName: string;
  category: string;
}

export const ContractsPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const contracts = useSelector(contractsSelector);
  const handledHash = React.useRef<string>("");

  React.useEffect(() => {
    dispatch(fetchTools());
  }, [dispatch]);

  const items = React.useMemo<IContractItem[]>(() => {
    if (!contracts || !contracts.categories) return [];

    const result: IContractItem[] = [];

    contracts.categories.forEach((category: any) => {
      (category.content || []).forEach((item: any) => {
        if (!item || !item.title || !item.entries) return;

        const url = `https:${item.entries}`;

        result.push({
          slug: toSlug(item.title),
          title: item.title,
          url,
          fileName: getFileName(url, item.title),
          category: category.name,
        });
      });
    });

    return result;
  }, [contracts]);

  const activeSlug = (location.hash || "").replace("#", "").toLowerCase();

  React.useEffect(() => {
    if (!activeSlug || !items.length) return;
    if (handledHash.current === activeSlug) return;

    const target = items.find((item) => item.slug === activeSlug);
    if (!target) return;

    handledHash.current = activeSlug;

    const node = document.getElementById(target.slug);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });

    downloadFile(target.url, target.fileName);
  }, [activeSlug, items]);

  return (
    <React.Fragment>
      <Helmet>
        <title>Contracts | Eurotech</title>
        <meta
          name="description"
          content="Eurotech cooperative and state contract program guides. Download the contract documents you need."
        />
      </Helmet>

      <Grid container className={styles.wrapper}>
        <Grid item xs={12} className={styles.header}>
          <Typography className={styles.title}>Contracts</Typography>
          <Typography className={styles.subtitle}>
            Program guides and contract documents. Select a contract to download
            the document.
          </Typography>
        </Grid>

        <Grid item xs={12} className={styles.list}>
          {items.map((item) => (
            <article
              id={item.slug}
              key={item.slug}
              className={
                activeSlug === item.slug
                  ? `${styles.card} ${styles.card_active}`
                  : styles.card
              }
            >
              <div className={styles.icon}>
                <img
                  src="/assets/images/header/tools/document.png"
                  alt="document"
                />
              </div>

              <div className={styles.info}>
                <Typography className={styles.name}>{item.title}</Typography>
                <Typography className={styles.file}>
                  {item.fileName}
                </Typography>
              </div>

              <button
                type="button"
                className={styles.download}
                onClick={() => downloadFile(item.url, item.fileName)}
              >
                Download
              </button>
            </article>
          ))}

          {!items.length ? (
            <Typography className={styles.empty}>
              Contract documents are being updated. Please check back shortly.
            </Typography>
          ) : null}
        </Grid>
      </Grid>

      <ContactArticle />
      <Footer />
    </React.Fragment>
  );
};

export default ContractsPage;
